import { userPreferencesApi } from '@/lib/api/user-preferences.api'
import { hashKey } from '@tanstack/react-query'

import type { BrowserNotificationPreferences } from '@scrambl/shared'
import { PreferenceContextEnum, PreferenceScopeEnum } from '@scrambl/shared'

import type { NotificationReaderContext } from './reads.api'
import {
  assertBrowserPreferenceRow,
  browserPreferenceInput,
} from './browser-preferences'
import {
  assertNotificationReaderContext,
  NotificationReadContractError,
} from './reads.api'

function validate(
  row: Awaited<ReturnType<typeof userPreferencesApi.getUserPreferences>>,
  context: NotificationReaderContext,
) {
  if (row === null) return null
  try {
    assertBrowserPreferenceRow(row!, context.userId, context.organizationId)
    return browserPreferenceInput.body(row!.preferences)
  } catch {
    throw new NotificationReadContractError()
  }
}
const options = (context: NotificationReaderContext) => ({
  organizationId: context.organizationId,
  signal: context.signal,
  silent: true,
})

/** Reader bodies belong to the original account; writes return no cacheable body. */
export const browserSettingsApi = {
  async read(context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    const row = await userPreferencesApi.getUserPreferences(
      PreferenceContextEnum.NOTIFICATIONS,
      undefined,
      PreferenceScopeEnum.USER,
      options(context),
    )
    assertNotificationReaderContext(context)
    return { preferences: validate(row, context) }
  },
  async save(
    value: BrowserNotificationPreferences,
    context: NotificationReaderContext,
  ): Promise<void> {
    assertNotificationReaderContext(context)
    const submitted = browserPreferenceInput.body(structuredClone(value))
    const row = await userPreferencesApi.saveUserPreferences(
      PreferenceContextEnum.NOTIFICATIONS,
      submitted,
      undefined,
      PreferenceScopeEnum.USER,
      options(context),
    )
    assertNotificationReaderContext(context)
    if (hashKey([validate(row, context)]) !== hashKey([submitted]))
      throw new NotificationReadContractError()
  },
}
